/**
 * @typedef {object} Cohort
 * @property {string} ATL_COHORT The cookie name
 * @property {object | null} readWriteAtlCohort The cohort object or null
 * @property {object | null} readAtlCohort The cohort object or null
 */
import { Cookie } from "../../utils/cookie";

import { Guid } from "./guid";
import { Helpers } from "./helpers";

export const ATL_COHORT = "atlCohort";
export const COHORT_VERSION = "2";
export const requiredBucketAttributeKeys: (keyof BucketAttributes)[] = [
    "index",
    "bucketedAtUTC",
    "version",
    "bucketId"
];

const cohortBuckets: {
    [key in BucketName]: Pick<BucketAttributes, "bucketId">;
} = {
    bucketAll: {
        bucketId: 0
    }
};

type BucketName = "bucketAll";

type BucketAttributes = {
    bucketedAtUTC: string;
    bucketId: number;
    index: number;
    version: string;
};

export type AtlCohort = {
    [key in BucketName]: BucketAttributes;
};

/**
 * Provides public access to cohort cookie name and methods for reading and writing that cookie
 * @module Cohort
 */
export class Cohort {
    /*
     Hypothetical way to add other buckets (adding their name to the types above):

        if (isMobileDevice()) {
            cohortBuckets.bucket_mobile = {
                bucketId: 1
            };
        }

        if (isEnglishSpeaking()) {
            cohortBuckets.bucket_english = {};
        }

        function isMobileDevice() {
            // some logic here...
            return true;
        }
    
        function isEnglishSpeaking() {
            // some logic here...
            return false;
        }
    */

    /**
     * Tries to read the ATL_COHORT cookie and return it
     * @function readAtlCohort
     * @return {object | null} the cohort object or null
     */
    public static readAtlCohort(): AtlCohort | null {
        let atlCohort;
        const atlCohortCookie = Cookie.readCookie(ATL_COHORT);

        try {
            atlCohort = atlCohortCookie
                ? (JSON.parse(atlCohortCookie) as AtlCohort)
                : null;
            return Helpers.isObject(atlCohort) ? atlCohort : null;
        } catch (e) {
            Helpers.sendWrapperLoadError("Error reading ALT_COHORT Cookie");
            return null;
        }
    }

    /**
     * Writes the stringified `atlCohort` object to the `ATL_COHORT` cookie
     * @function writeAtlCohort
     * @ignore
     * @param {object} atlCohort the cohort object
     */
    public static writeAtlCohort(atlCohort: AtlCohort) {
        if (Helpers.isObject(atlCohort)) {
            Cookie.writeCookie(
                ATL_COHORT,
                JSON.stringify(atlCohort),
                3650,
                true
            );
        }
    }

    /**
     * Checks to see if a bucket is in the `atlCohort` object and returns `true` if it does and `false` if it does not
     * @function bucketExists
     * @ignore
     * @param  {BucketName} key a bucket key
     * @param  {Partial<AtlCohort>} atlCohort the cohort object
     * @return {boolean} whether or not a bucket is in the cohort object
     */
    public static bucketExists(key: BucketName, atlCohort: Partial<AtlCohort>) {
        if (Helpers.isObject(atlCohort)) {
            const bucket = atlCohort[key];
            if (bucket && Helpers.isObject(bucket)) {
                return requiredBucketAttributeKeys.every(
                    (bucketAttribute) =>
                        typeof bucket[bucketAttribute] !== "undefined"
                );
            }
        }

        return false;
    }

    /**
     * Takes a `bucketKey` and returns and object with `index` and `bucketedAtUTC` properties and optional attribute properties
     * @function createBucket
     * @ignore
     * @param  {string} bucketKey
     * @param  {string} [anonymousIdOverride] optional pre-read anonymousId (e.g. from SSR request cookies),
     *                  used to avoid falling back to Math.random() when document.cookie is unavailable
     * @return {object} a bucket object
     */
    public static createBucket(
        bucketKey: BucketName,
        anonymousIdOverride?: string
    ) {
        const bucketAttributes = cohortBuckets[bucketKey];
        const anonymousId =
            anonymousIdOverride ?? Cookie.readCookie("ajs_anonymous_id");
        const strippedAnonymousId = decodeURIComponent(
            anonymousId || ""
        ).replace(/"/g, "");
        const bucket: BucketAttributes = {
            bucketedAtUTC: new Date().toISOString(),
            version: COHORT_VERSION,
            index: strippedAnonymousId
                ? Guid.guidHashCode(
                      Guid.addNumberToGUID(
                          strippedAnonymousId,
                          bucketAttributes.bucketId
                      )
                  ) % 100
                : Math.ceil(Math.random() * 100),
            bucketId: bucketAttributes.bucketId
        };

        return bucket;
    }

    /**
     * Reads `ATL_COHORT` cookie and if there are any missing buckets, it adds them and writes them to the cookie. Returns the `atlCohort` object
     * @function readWriteAtlCohort
     * @param {boolean} writeOverride when present, trigger a cohort rewrite
     * @return {object | null} the cohort object or null
     */
    public static readWriteAtlCohort(writeOverride?: boolean) {
        // if overriding the cohort, do not read the existing cohort; set to empty to force a write
        const atlCohort: Partial<AtlCohort> = writeOverride
            ? {}
            : Cohort.readAtlCohort() || {};

        const missingBuckets = (Object.keys(cohortBuckets) as BucketName[])
            // Find missing buckets
            .filter((bucket) => !Cohort.bucketExists(bucket, atlCohort));

        if (missingBuckets.length) {
            missingBuckets.forEach(
                (missingBucket) =>
                    (atlCohort[missingBucket] =
                        Cohort.createBucket(missingBucket))
            );
            Cohort.writeAtlCohort(atlCohort as AtlCohort);
        }

        return atlCohort as AtlCohort;
    }
}
